import { useIsMobile } from '@common/hooks/use-is-mobile';
import { Button } from '@/components/ui/button';
import { Mascot } from '@common-components/mascot/Mascot';
import { namespace } from '@common-config/constants';
import { AlertCircle, ArrowDown, FileUp, Paperclip } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCEPT_ATTR, AiChatAttachmentsOptions, MAX_ATTACHMENTS_PER_MESSAGE, UploadedAttachment, validateAttachment } from './attachments';
import { AttachmentChip, AttachmentChips } from './AttachmentChips';
import { pacedStatus, secondsBetween } from './phase-labels';
import {
  AiStatusLine,
  ChatBubble,
  ChatMenu,
  NarrationLine,
  PromptComposer,
  PromptComposerHandle,
  SuggestionChips,
  ThinkingBlock,
  ToolChip,
  ToolSummary,
  TraceLog,
  useFormatElapsed,
  useToolLabel,
} from './parts';
import { AiChatItem, BUY_CREDITS_PATH, isNoCreditsNotice, toolDisplayName } from './transcript';
import { AI_STREAM_RUNNING, AiStreamState, AiStreamStep } from './useAiStream';
import { useDetailLevel } from './useDetailLevel';
import { useStickToBottom } from './useStickToBottom';

/**
 * THE AI chat — one component for every surface that talks to an AI agent
 * (btgpt, the agent builder: new + edit). It owns only view state: the draft,
 * scroll pinning, the "tools" disclosure, and the ONE clock of the status line.
 * The surface owns the data: durable `items`, whether a turn is `busy`, and the
 * live `stream` (from `useAiStream`). Surface-only widgets (btgpt board blocks,
 * settings pickers) come in through `renderExtra`; surface menu entries through `menu`.
 *
 * While a turn runs there is exactly ONE status line (`ai-status`) under the last
 * live item, at every detail level ("Show while it works", `useDetailLevel`):
 *   quiet    the status line only; the answer appears whole at `done`
 *   activity + the steps (narration, tool chips, a failed step) and the answer as it is written
 *   thinking + the summarized thinking (live only)
 *   trace    + the timestamped technical log
 */

/** The live part of a turn, as `useAiStream` reports it. */
export type AiChatStream = Pick<
  AiStreamState,
  'phase' | 'infra' | 'textSoFar' | 'steps' | 'log' | 'error' | 'failedFrom' | 'startedAt' | 'stepStartedAt' | 'lastByteAt' | 'thinkingTokens'
>;

export interface AiChatComposerOptions {
  /** Default: the shared "Message Blueticks AI…". */
  placeholder?: string;
  /** Lock the input (feature unavailable, no connection). */
  disabled?: boolean;
  /** Start side of the composer footer (btgpt: attach). */
  leading?: React.ReactNode;
  footer?: React.ReactNode;
}

export interface AiChatProps {
  /** The durable conversation. */
  items: AiChatItem[];
  /** Only the workspace owner can buy credits (default true); others see "ask the owner" under the out-of-credits notice; null = not known yet, shows neither. */
  canBuyCredits?: boolean | null;
  /**
   * Resolving to exactly `false` = the send failed: the text and any attachment
   * chips go back into an empty composer. `attachments` is always passed, `[]` when none.
   */
  onSend: (text: string, attachments: UploadedAttachment[]) => void | boolean | Promise<void | boolean>;
  /**
   * Turns on file attachments: a paperclip in the composer, drag-and-drop anywhere
   * on the chat (replacing `onDropFiles`), pasting an image, and upload chips.
   * Absent = the feature is off (no paperclip, no drop, no paste) — unchanged behavior.
   */
  attachments?: AiChatAttachmentsOptions;
  /** A turn is running: sending waits and the busy row shows until the stream has something to say. */
  busy?: boolean;
  /**
   * Stop the running turn (like ChatGPT/Claude). When set AND `busy`, the send
   * button becomes an always-clickable Stop button and Esc in the composer
   * stops too. Absent = unchanged behavior (a spinner while busy).
   */
  onStop?: () => void | Promise<void>;
  /** The live turn (null/absent: no stream — e.g. live view off on a polled surface). */
  stream?: AiChatStream | null;
  /** Start side of the header bar: title, model picker, a drawer button. */
  header?: React.ReactNode;
  /** Extra entries in the header `⋯` menu, under "Show while it works". */
  menu?: React.ReactNode;
  /** Shown while there are no items; the composer sits centred under it (docked on phones). */
  empty?: React.ReactNode;
  /** Example prompts under the empty state; a pick fills the composer. */
  suggestions?: string[];
  /** Surface widgets under an item (keyed by `item.id`). */
  renderExtra?: (item: AiChatItem) => React.ReactNode;
  composer: AiChatComposerOptions;
  /** Files dropped anywhere on the chat (back-compat; ignored once `attachments` is set — that drop goes through the chip flow). */
  onDropFiles?: (files: FileList) => void;
  /** 'lg' = a page (centred ≤768 px column), 'md' = a side panel. */
  size?: 'lg' | 'md';
  dir?: 'ltr' | 'rtl';
  /**
   * Keep the send button clear of the Crisp launcher, which sits in the bottom
   * inline-end corner (Crisp mirrors to the left in RTL). Default: on phones.
   * Pass true when the chat pane reaches that corner of the page.
   */
  cornerClearance?: boolean;
}

export interface AiChatHandle {
  focus: () => void;
  getDraft: () => string;
  setDraft: (text: string) => void;
}

/**
 * THE clock: a tick every second while `active` — the status line's step / turn times
 * and its liveness check — plus one exact wake-up at `wakeAt` (the next paced step).
 */
function useNow(active: boolean, wakeAt: number | null = null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  useEffect(() => {
    if (!active || wakeAt === null) return;
    const id = window.setTimeout(() => setNow(Date.now()), Math.max(0, wakeAt - Date.now()));
    return () => window.clearTimeout(id);
  }, [active, wakeAt]);
  return now;
}

/** When `busy` last turned on (the fallback clock when there is no stream). */
function useSince(busy: boolean): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => setSince(busy ? Date.now() : null), [busy]);
  return since;
}

/** The out-of-credits notice's call to action: straight to the add-credits dialog. */
const BuyCreditsButton: React.FC<{ canBuy: boolean | null }> = ({ canBuy }) => {
  const { t } = useTranslation(namespace);
  // null = who the user is in this workspace is not loaded yet: show nothing rather than a wrong hint.
  if (canBuy === null) return null;
  // Credits are bought by the workspace owner; a member has no page to go to, so say who can.
  if (!canBuy) {
    return (
      <p className="m-0 text-sm text-ai-text-secondary" data-testid="ai-ask-owner-credits">
        {t('ai.chat.askOwnerForCredits')}
      </p>
    );
  }
  // One row of options under the notice: further purchase options (e.g. an unlimited add-on) go beside the button.
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ai-credits-actions">
      <Button asChild size="sm" className="w-fit" data-testid="ai-buy-credits">
        <a href={BUY_CREDITS_PATH}>{t('ai.chat.buyCredits')}</a>
      </Button>
    </div>
  );
};

/** One assistant turn: the orb, its tools (collapsed), its answer, then surface extras. */
const AssistantTurn: React.FC<{
  tools: string[];
  item?: AiChatItem & { kind: 'assistant' };
  size: 'lg' | 'md';
  extra?: React.ReactNode;
  /** The latest reply's face breathes and blinks; older ones hold still. */
  latest?: boolean;
  /** Whether this user can buy credits (workspace owner); false swaps the button for a hint. */
  canBuyCredits?: boolean | null;
}> = ({ tools, item, size, extra, latest, canBuyCredits = true }) => (
  <div className="flex gap-3 animate-in fade-in duration-(--ai-duration-enter) ease-ai">
    <div className="shrink-0 pt-0.5">
      <Mascot size={24} level={latest ? 'ambient' : 'static'} />
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <ToolSummary names={tools} />
      {item?.error ? (
        <div className="flex items-start gap-2 text-ai-text-secondary" role="alert" data-testid="ai-msg-error">
          <AlertCircle className="mt-1 h-4 w-4 shrink-0" />
          <span dir="auto" className="whitespace-pre-wrap">
            {item.text}
          </span>
        </div>
      ) : item?.text ? (
        <ChatBubble mine={false} size={size}>
          {item.text}
        </ChatBubble>
      ) : null}
      {item?.error && isNoCreditsNotice(item.text) && <BuyCreditsButton canBuy={canBuyCredits} />}
      {extra}
    </div>
  </div>
);

/**
 * The soft caret, inline after the last word: on a trailing paragraph / heading /
 * quote, or on the last item of a trailing list. None after a code block or table
 * (after a block it would sit on a line of its own).
 */
const STREAMING_CARET = [
  "[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:content-['']",
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:inline-block',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:ms-1',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:h-[1em]',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:w-[0.45em]',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:rounded-sm',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:align-[-0.12em]',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:bg-ai-text-faint',
  '[&_.ai-markdown>:last-child:is(p,h1,h2,h3,h4,blockquote)]:after:animate-pulse',
  "[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:content-['']",
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:inline-block',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:ms-1',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:h-[1em]',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:w-[0.45em]',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:rounded-sm',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:align-[-0.12em]',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:bg-ai-text-faint',
  '[&_.ai-markdown>:last-child:is(ul,ol)>li:last-child]:after:animate-pulse',
].join(' ');

export const AiChat = forwardRef<AiChatHandle, AiChatProps>(function AiChat(
  {
    items,
    canBuyCredits = true,
    onSend,
    busy = false,
    onStop,
    stream,
    header,
    menu,
    empty,
    suggestions,
    renderExtra,
    composer,
    onDropFiles,
    attachments,
    size = 'lg',
    dir,
    cornerClearance,
  },
  ref
) {
  const { t } = useTranslation(namespace);
  const isMobile = useIsMobile();
  const toolLabel = useToolLabel();
  const formatElapsed = useFormatElapsed();
  const [level] = useDetailLevel();
  const [draft, setDraft] = useState('');
  const [dragging, setDragging] = useState(false); // legacy ring, only driven when `attachments` is absent (onDropFiles back-compat)
  const [dropOverlay, setDropOverlay] = useState(false); // the full-area "Drop files here" overlay, attachments-only
  const dragDepthRef = useRef(0);
  const [chips, setChips] = useState<AttachmentChip[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<PromptComposerHandle | null>(null);
  const { scrollRef, contentRef, showJump, scrollToBottom } = useStickToBottom<HTMLDivElement, HTMLDivElement>();

  // Full-area drop overlay (attachments only): a dragenter/dragleave depth counter so hovering over a
  // child inside the chat (bubbling enter/leave pairs) never flickers it away — only net-zero hides it.
  const resetDropOverlay = useCallback(() => {
    dragDepthRef.current = 0;
    setDropOverlay(false);
  }, []);
  useEffect(() => {
    if (!attachments || typeof window === 'undefined') return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetDropOverlay();
    };
    const onWindowDragEnd = () => resetDropOverlay();
    const onWindowDrop = () => resetDropOverlay();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('dragend', onWindowDragEnd);
    window.addEventListener('drop', onWindowDrop);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('dragend', onWindowDragEnd);
      window.removeEventListener('drop', onWindowDrop);
    };
  }, [attachments, resetDropOverlay]);

  // Adds files as chips (capped at MAX_ATTACHMENTS_PER_MESSAGE total, extras become an error chip)
  // and starts uploading each valid one immediately.
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      if (!attachments) return;
      const list = Array.from(files);
      setChips((prev) => {
        const next = [...prev];
        list.forEach((file) => {
          const id = `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          if (next.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
            next.push({ id, name: file.name, mime: file.type, size: file.size, status: 'error', error: 'max 10 files per message', file });
            return;
          }
          const reason = validateAttachment(file);
          if (reason) {
            next.push({ id, name: file.name, mime: file.type, size: file.size, status: 'error', error: reason, file });
            return;
          }
          next.push({ id, name: file.name, mime: file.type, size: file.size, status: 'uploading', file });
          attachments.upload(file).then(
            (uploaded) => setChips((cur) => cur.map((c) => (c.id === id ? { ...c, status: 'ready', uploaded } : c))),
            (err: unknown) => {
              const message = err instanceof Error && err.message ? err.message : 'upload failed';
              setChips((cur) => cur.map((c) => (c.id === id ? { ...c, status: 'error', error: message } : c)));
            }
          );
        });
        return next;
      });
    },
    [attachments]
  );
  const removeChip = useCallback((id: string) => setChips((cur) => cur.filter((c) => c.id !== id)), []);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  useImperativeHandle(
    ref,
    () => ({
      focus: () => composerRef.current?.focus(),
      getDraft: () => draftRef.current,
      setDraft,
    }),
    []
  );

  // The ⋯ menu ("Show while it works" first) shows only when the surface passes `menu` (any node, even
  // an empty fragment). A page that hosts <ChatMenu/> in its own header passes neither.
  const showMenu = !!menu;
  const phase = stream?.phase ?? 'idle';
  // Live = a turn is running, or the surface still waits for its saved turn. Settled
  // = the answer is in `items` (never paint it twice). A `done` stream while the
  // surface is still busy (the builder's poll lags `done` by a few seconds) keeps
  // its live turn on screen, still: no caret.
  const live = AI_STREAM_RUNNING.has(phase) || busy;
  const finished = phase === 'done';
  const isEmpty = items.length === 0 && !live;
  const docked = !isEmpty || isMobile;
  const lg = size === 'lg';

  const since = useSince(live);
  // The next paced step's time (see `pacedStatus`): the clock wakes exactly then.
  const [wakeAt, setWakeAt] = useState<number | null>(null);
  const now = useNow(live, wakeAt);
  const paced = live ? pacedStatus(stream, busy, now) : null;
  const status = paced?.view ?? null;
  const nextWake = paced?.wakeAt ?? null;
  useEffect(() => setWakeAt(nextWake), [nextWake]);

  const readyAttachments = chips.filter((c) => c.status === 'ready' && c.uploaded).map((c) => c.uploaded as UploadedAttachment);
  const attachmentsUploading = chips.some((c) => c.status === 'uploading');

  const send = useCallback(() => {
    const text = draft.trim();
    if ((!text && readyAttachments.length === 0) || attachmentsUploading || busy || composer.disabled) return;
    const sentChips = chips;
    setDraft('');
    setChips([]);
    void Promise.resolve(onSend(text, readyAttachments)).then((ok) => {
      if (ok === false) {
        if (!draftRef.current) setDraft(text);
        setChips((cur) => (cur.length === 0 ? sentChips : cur));
      }
    });
    scrollToBottom();
  }, [draft, chips, readyAttachments, attachmentsUploading, busy, composer.disabled, onSend, scrollToBottom]);

  const pick = useCallback((s: string) => {
    setDraft(s);
    composerRef.current?.focus();
  }, []);

  /* ── durable rows: consecutive tools fold into the assistant turn that follows ── */
  const rows: React.ReactNode[] = [];
  let pendingTools: string[] = [];
  const flushTools = (key: string) => {
    if (pendingTools.length) rows.push(<AssistantTurn key={key} tools={pendingTools} size={size} />);
    pendingTools = [];
  };
  const latestReplyId = [...items].reverse().find((it) => it.kind === 'assistant')?.id;
  items.forEach((it) => {
    if (it.kind === 'tool') {
      pendingTools.push(it.name);
      return;
    }
    if (it.kind === 'user') {
      flushTools(`tools-${it.id}`);
      rows.push(
        <React.Fragment key={it.id}>
          <div className="flex flex-col items-end gap-1.5 self-end">
            <ChatBubble mine size={size}>
              {it.text ?? <span className="italic opacity-80">{t('ai.chat.earlierMessage')}</span>}
            </ChatBubble>
            {it.attachments && it.attachments.length > 0 && (
              <AttachmentChips
                chips={it.attachments.map((a, i) => ({ id: `${it.id}-att-${i}`, name: a.name, mime: a.mime, status: 'ready' as const }))}
              />
            )}
          </div>
          {renderExtra?.(it)}
        </React.Fragment>
      );
      return;
    }
    rows.push(
      <AssistantTurn
        key={it.id}
        tools={pendingTools}
        item={it}
        size={size}
        extra={renderExtra?.(it)}
        latest={!live && it.id === latestReplyId}
        canBuyCredits={canBuyCredits}
      />
    );
    pendingTools = [];
  });
  flushTools('tools-end');

  /* ── the live turn ── */
  let liveTurn: React.ReactNode = null;
  if (live) {
    const steps = stream?.steps ?? [];
    // Quiet shows only the status line while it works; the answer appears whole at `done`.
    const showSteps = level !== 'quiet';
    const showThinking = level === 'thinking' || level === 'trace';
    const text = showSteps || finished ? stream?.textSoFar ?? '' : '';
    const writing = phase === 'streaming' && !!text;
    // A tool is a chip once it is done: ended (`tool_done`), or — with no id to wait for
    // (an old stream) — superseded. The one the status line talks about is not a chip yet.
    const current = status?.stepId;
    const lastToolId = [...steps].reverse().find((s) => s.kind === 'tool')?.id;
    const isChip = (step: Extract<AiStreamStep, { kind: 'tool' }>) =>
      step.id !== current && (step.endedAt !== undefined || (!step.toolUseId && !(phase === 'tool' && step.id === lastToolId)));

    const rows: React.ReactNode[] = [];
    if (showSteps) {
      let run: { step: Extract<AiStreamStep, { kind: 'tool' }>; count: number } | null = null;
      const flush = () => {
        if (run) rows.push(<ToolChip key={run.step.id} name={run.step.name} count={run.count} failed={run.step.isError} />);
        run = null;
      };
      steps.forEach((step) => {
        if (step.kind === 'tool') {
          if (!isChip(step)) return;
          const same = run && toolDisplayName(run.step.name) === toolDisplayName(step.name) && !!run.step.isError === !!step.isError;
          if (same && run) run.count++;
          else {
            flush();
            run = { step, count: 1 };
          }
          return;
        }
        if (step.kind === 'thinking' && (!showThinking || !step.text.trim())) return;
        flush();
        rows.push(
          step.kind === 'narration' ? (
            <NarrationLine key={step.id} text={step.text} />
          ) : (
            <ThinkingBlock
              key={step.id}
              text={step.text}
              open={step.endedAt === undefined && phase === 'thinking'}
              seconds={secondsBetween(step.startedAt, step.endedAt ?? now)}
            />
          )
        );
      });
      flush();
    }

    let statusLine: React.ReactNode = null;
    if (status) {
      const stepTime = status.stepSince !== null ? formatElapsed(secondsBetween(status.stepSince, now)) : '';
      const totalTime = formatElapsed(secondsBetween(stream?.startedAt ?? since, now));
      const params = { ...status.params, tool: status.params.tool ? toolLabel(status.params.tool) : undefined };
      const label = t(status.labelKey, params); // i18n-dynamic-key: ai.status.* key table in phase-labels.ts
      statusLine = (
        <AiStatusLine
          label={label}
          step={stepTime}
          total={totalTime !== stepTime ? totalTime : ''}
          tone={status.tone}
        />
      );
    }

    const hasContent = rows.length > 0 || !!text;
    liveTurn = (
      <div className="flex flex-col gap-2" data-testid="ai-live-turn" data-level={level}>
        {hasContent && (
          <div
            className={`flex min-w-0 flex-col gap-2 ps-9 ${writing ? STREAMING_CARET : ''}`}
            data-streaming={writing ? 'true' : undefined}
          >
            {rows.length > 0 && <div className="flex flex-col gap-1">{rows}</div>}
            {text && (
              <ChatBubble mine={false} size={size}>
                {text}
              </ChatBubble>
            )}
          </div>
        )}
        {level === 'trace' && (stream?.log.length ?? 0) > 0 && (
          <div className="ps-9">
            <TraceLog steps={steps} log={stream?.log ?? []} startedAt={stream?.startedAt ?? null} now={now} />
          </div>
        )}
        {statusLine}
      </div>
    );
  }

  const attachUi = attachments ? (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = ''; // allow re-selecting the same file
        }}
        aria-label={t('ai.chat.attachFile', 'Attach a file')}
        data-testid="ai-attach-input"
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        aria-label={t('ai.chat.attachFile', 'Attach a file')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ai-text-secondary transition-colors hover:bg-ai-muted hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
        data-testid="ai-attach-button"
      >
        <Paperclip className="h-[18px] w-[18px]" />
      </button>
    </>
  ) : null;

  const composerEl = (
    <PromptComposer
      ref={composerRef}
      size={size}
      value={draft}
      onChange={setDraft}
      onSubmit={send}
      placeholder={composer.placeholder ?? t('ai.chat.placeholder')}
      disabled={composer.disabled}
      busy={busy}
      onStop={onStop}
      leading={
        attachUi ? (
          <>
            {attachUi}
            {composer.leading}
          </>
        ) : (
          composer.leading
        )
      }
      footer={composer.footer}
      sendLabel={t('ai.chat.send')}
      autoFocus={!isMobile && lg}
      dir={dir}
      attachmentsRow={attachments && chips.length > 0 ? <AttachmentChips chips={chips} onRemove={removeChip} /> : undefined}
      hasReadyAttachment={attachments ? readyAttachments.length > 0 : undefined}
      attachmentsUploading={attachments ? attachmentsUploading : undefined}
      onPasteFiles={attachments ? addFiles : undefined}
    />
  );

  return (
    <div
      dir={dir}
      className="relative flex h-full min-h-0 flex-col bg-ai-bg text-ai-text"
      data-testid="ai-chat"
      onDragEnter={
        attachments
          ? (e) => {
              if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
              e.preventDefault();
              dragDepthRef.current += 1;
              setDropOverlay(true);
            }
          : undefined
      }
      onDragOver={
        attachments
          ? (e) => {
              if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) {
                e.preventDefault();
                // Stop the native drag from bubbling to a host page's own
                // listeners (e.g. WhatsApp Web's own drop handling when this
                // chat renders inside the extension's dialog).
                e.stopPropagation();
              }
            }
          : onDropFiles
            ? (e) => {
                e.preventDefault();
                if (!dragging) setDragging(true);
              }
            : undefined
      }
      onDragLeave={
        attachments
          ? () => {
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
              if (dragDepthRef.current === 0) setDropOverlay(false);
            }
          : onDropFiles
            ? () => setDragging(false)
            : undefined
      }
      onDrop={
        attachments
          ? (e) => {
              e.preventDefault();
              // Same reasoning as onDragOver above: keep the drop from also
              // reaching a host page's own listeners.
              e.stopPropagation();
              resetDropOverlay();
              if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
            }
          : onDropFiles
            ? (e) => {
                e.preventDefault();
                setDragging(false);
                if (e.dataTransfer.files?.length) onDropFiles(e.dataTransfer.files);
              }
            : undefined
      }
    >
      {attachments && dropOverlay && (
        <div
          className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-ai-bg/85 backdrop-blur-[2px] animate-in fade-in duration-(--ai-duration-fast)"
          data-testid="ai-drop-overlay"
        >
          <div className="flex flex-col items-center gap-3 rounded-ai-md border-2 border-dashed border-ai-accent bg-ai-surface px-10 py-8 text-center shadow-xl">
            <FileUp className="h-8 w-8 text-ai-accent" />
            <div className="flex flex-col gap-1">
              <p className="text-[15px] font-semibold text-ai-text">{t('ai.chat.dropTitle')}</p>
              <p className="text-sm text-ai-text-secondary">{t('ai.chat.dropSubtitle')}</p>
            </div>
          </div>
        </div>
      )}
      {(header || showMenu) && (
        <header className={`flex shrink-0 items-center gap-2 border-b border-ai-border ${lg ? 'h-14 px-3 sm:px-4' : 'h-12 px-3'}`}>
          <div className="flex min-w-0 flex-1 items-center gap-2">{header}</div>
          {showMenu && <ChatMenu dir={dir}>{menu}</ChatMenu>}
        </header>
      )}

      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} className="h-full overflow-y-auto overflow-x-hidden overscroll-contain" role="log" aria-busy={live}>
          <div
            ref={contentRef}
            className={`mx-auto flex min-h-full w-full flex-col ${lg ? 'max-w-[768px] gap-6 px-4 py-6 sm:px-6' : 'gap-5 px-4 py-4'}`}
          >
            {isEmpty ? (
              // Optical centre: the free space splits 2:3 above/below, so the hero sits a
              // little above the middle (a true centre reads low). Short frames: no spacer room, top-down.
              <div className="flex flex-1 flex-col">
                <div aria-hidden className="flex-[2]" />
                <div className="flex flex-col items-stretch gap-6 py-8">
                  {empty}
                  {!docked && composerEl}
                  {suggestions && suggestions.length > 0 && <SuggestionChips items={suggestions} onPick={pick} size={size} />}
                </div>
                <div aria-hidden className="flex-[3]" />
              </div>
            ) : (
              <>
                {rows}
                {liveTurn}
              </>
            )}
          </div>
        </div>
        {showJump && !isEmpty && (
          <button
            type="button"
            onClick={() => scrollToBottom()}
            className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-ai-border-strong bg-ai-surface px-3 py-1.5 text-xs font-medium text-ai-text shadow-lg animate-in fade-in slide-in-from-bottom-1 outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
            data-testid="ai-jump-latest"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {t('ai.chat.jumpToLatest')}
          </button>
        )}
      </div>

      {docked && (
        <div
          // Start/end padding set separately so the Crisp clearance (inline-end) is never overridden by a breakpoint px-*.
          className={`shrink-0 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] ${lg ? 'ps-4 sm:ps-6' : 'ps-3'} ${
            (cornerClearance ?? isMobile) ? 'pe-[76px]' : lg ? 'pe-4 sm:pe-6' : 'pe-3'
          }`}
        >
          <div className={`mx-auto w-full ${lg ? 'max-w-[768px]' : ''} ${dragging ? 'rounded-ai-composer ring-2 ring-ai-accent' : ''}`}>
            {composerEl}
          </div>
        </div>
      )}
    </div>
  );
});
